import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardList, BadgeCheck, Users, Hourglass, Eye, Mail, UserPlus } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const DEPT_COURT = { "RH": "RH", "Comptabilité & Paie": "Compta & Paie", "Fiscalité": "Fiscalité", "Direction": "Direction" };
const GOLD = "#f9b233";
const BLUE = "#1587b8";
const CORRECT = "#e05d5d";
const MUTED = "#cbd5e1";

export default function StaffStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats")
      .then((r) => setStats(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  if (!stats) {
    return <p className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>;
  }

  const kpis = [
    { id: "works", icon: ClipboardList, value: stats.total_works, label: "Travaux enregistrés" },
    { id: "parfaits", icon: BadgeCheck, value: stats.parfaits, label: "Validés « parfait »" },
    { id: "active", icon: Users, value: stats.members_active, label: "Membres actifs" },
    { id: "pending", icon: Hourglass, value: stats.members_pending, label: "Demandes en attente" },
  ];
  const deptData = stats.by_dept.map((d) => ({
    ...d,
    court: DEPT_COURT[d.dept] || d.dept,
    par_membre: d.membres ? Math.round((d.total / d.membres) * 10) / 10 : 0,
  }));
  const monthData = stats.by_month.map((m) => ({
    ...m,
    label: MOIS[parseInt(m.month.slice(5, 7), 10) - 1],
  }));
  const statusData = [
    { name: "Terminé", value: stats.by_status["Terminé"] || 0, color: BLUE },
    { name: "En cours", value: stats.by_status["En cours"] || 0, color: GOLD },
  ];
  const visitDays = (stats.visits?.by_day || []).map((d) => ({
    ...d,
    label: `${d.day.slice(8, 10)}/${d.day.slice(5, 7)}`,
  }));
  const visits = stats.visits || { total_views: 0, unique_visitors: 0, by_day: [], top_paths: [] };
  const demandes = stats.demandes || { messages_total: 0, messages_new: 0, signups_total: 0, signups_pending: 0 };

  return (
    <div className="space-y-6" data-testid="stats-view">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((k) => (
          <div key={k.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`stats-kpi-${k.id}`}>
            <k.icon size={18} className="text-korban-blue" />
            <p className="mt-2 font-display text-3xl font-black text-korban-navy">{k.value}</p>
            <p className="text-xs font-semibold text-slate-500">{k.label}</p>
          </div>
        ))}
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-chart-dept">
        <h2 className="font-display text-lg font-bold text-korban-navy">Travaux par département</h2>
        <p className="mb-4 text-xs text-slate-500">Répartition par évaluation (parfait, à corriger, en attente)</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={deptData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="court" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip cursor={{ fill: "rgba(18,57,92,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="parfaits" name="Parfait" stackId="a" fill={GOLD} />
              <Bar dataKey="corriger" name="À corriger" stackId="a" fill={CORRECT} />
              <Bar dataKey="attente" name="En attente" stackId="a" fill={MUTED} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-activity-dept">
        <h2 className="font-display text-lg font-bold text-korban-navy">Comparaison d'activité par département</h2>
        <p className="mb-4 text-xs text-slate-500">Travaux, tâches traitées, effectif et productivité de chaque département</p>
        <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                <XAxis dataKey="court" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip cursor={{ fill: "rgba(18,57,92,0.05)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="termines" name="Travaux terminés" fill={BLUE} radius={[3, 3, 0, 0]} />
                <Bar dataKey="encours" name="Travaux en cours" fill={GOLD} radius={[3, 3, 0, 0]} />
                <Bar dataKey="taches_faites" name="Tâches traitées" fill="#12395c" radius={[3, 3, 0, 0]} />
                <Bar dataKey="membres" name="Membres actifs" fill={MUTED} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={deptData} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f6" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis type="category" dataKey="court" width={84} tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                <Tooltip cursor={{ fill: "rgba(18,57,92,0.05)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="par_membre" name="Travaux par membre actif" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {stats.total_works === 0 && (
          <p className="mt-2 text-center text-xs text-slate-400">Aucune activité enregistrée pour l'instant.</p>
        )}
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.4fr_0.6fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-chart-month">
          <h2 className="font-display text-lg font-bold text-korban-navy">Évolution sur 6 mois</h2>
          <p className="mb-4 text-xs text-slate-500">Nombre de travaux enregistrés par mois</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="aireMois" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BLUE} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BLUE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip />
                <Area type="monotone" dataKey="total" name="Travaux" stroke={BLUE} strokeWidth={2.5} fill="url(#aireMois)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-chart-status">
          <h2 className="font-display text-lg font-bold text-korban-navy">Statuts</h2>
          <p className="mb-4 text-xs text-slate-500">Répartition des travaux</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3} strokeWidth={0}>
                  {statusData.map((s) => (
                    <Cell key={s.name} fill={s.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {stats.total_works === 0 && (
            <p className="text-center text-xs text-slate-400">Aucun travail enregistré pour l'instant.</p>
          )}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-traffic-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg font-bold text-korban-navy">Trafic du site (14 derniers jours)</h2>
          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
            <p className="flex items-center gap-1.5"><Eye size={15} className="text-korban-blue" /> Vues : <b data-testid="traffic-views">{visits.total_views}</b></p>
            <p className="flex items-center gap-1.5"><Users size={15} className="text-korban-blue" /> Visiteurs uniques : <b data-testid="traffic-unique">{visits.unique_visitors}</b></p>
          </div>
        </div>
        <div className="mt-4 h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={visitDays} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="aireTrafic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={GOLD} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip />
              <Area type="monotone" dataKey="views" name="Vues" stroke={GOLD} strokeWidth={2.5} fill="url(#aireTrafic)" />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke={BLUE} strokeWidth={2} fill="url(#aireTrafic)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {visits.top_paths.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-bold text-korban-navy">Pages les plus vues</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {visits.top_paths.map((p) => (
                <span key={p.path} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600" data-testid={`top-path-${p.path.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  {p.path === "/" ? "Accueil" : p.path === "/connexion" ? "Espace personnel (connexion)" : p.path} — {p.views} vue(s)
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="stats-demandes-section">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-korban-navy">
          <Mail size={18} className="text-korban-blue" /> Demandes reçues
        </h2>
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { id: "messages", icon: Mail, value: demandes.messages_total, label: "Messages de contact" },
            { id: "messages-new", icon: Mail, value: demandes.messages_new, label: "Messages non traités" },
            { id: "signups", icon: UserPlus, value: demandes.signups_total, label: "Inscriptions au portail" },
            { id: "access", icon: Hourglass, value: demandes.signups_pending, label: "Accès à activer" },
          ].map((k) => (
            <div key={k.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`demandes-kpi-${k.id}`}>
              <k.icon size={17} className="text-korban-blue" />
              <p className="mt-2 font-display text-2xl font-black text-korban-navy">{k.value}</p>
              <p className="text-xs font-semibold text-slate-500">{k.label}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
